<header class="landing-header">
  <a class="brand" routerLink="/">
    <img src="/appstrax-logo-icon.png" alt="Machine Logo" class="logo">
    <span class="brand-name">The Machine</span>
  </a>
</header>

<main class="landing">
  <section class="hero">
    <div class="hero-carousel" role="region" aria-label="Highlights">
      @for (s of heroSlides; track s.key; let i = $index) {
        <div
          class="hero-slide"
          [class.active]="i === activeHeroSlideIndex"
          [attr.aria-hidden]="i === activeHeroSlideIndex ? 'false' : 'true'"
          [style.backgroundImage]="getHeroBackground(s.image)"
        >
          <div class="hero-content">
            <h1>{{ s.title }}</h1>
            <p class="sub">{{ s.blurb }}</p>
          </div>
        </div>
      }
    </div>
  </section>

  <section class="audience">
    <h2>Who it's for</h2>
    <p class="lead">The Machine is for anyone who wants to go from concept to working software — fast.</p>
    <p class="section-desc">
      The Machine adapts to your role: developers automate scaffolding, tests, and pipelines; founders and clients validate scope with clear deliverables; teams align on a single source of truth. When you need more, assign Heroes—senior engineers who guide architecture and integrations—so you can ship with confidence at any scale.
    </p>
    <div class="grid">
      <div class="card audience-card">
        <div class="icon-circle"><i class="bi bi-code-slash"></i></div>
        <h3>Developers</h3>
        <p class="description">Ship faster with designs, tests, and Dev‑Ops pipelines done for you.</p>
        <div class="votes">
          <button type="button" class="vote-btn up" title="Like" (click)="vote('audience-developers', 1)" [disabled]="isVoting('audience-developers')"><i class="bi bi-hand-thumbs-up"></i></button>
          <button type="button" class="vote-btn down" title="Dislike" (click)="vote('audience-developers', -1)" [disabled]="isVoting('audience-developers')"><i class="bi bi-hand-thumbs-down"></i></button>
          <span class="vote-count">{{ getTally('audience-developers') }}</span>
        </div>
      </div>
      <div class="card audience-card">
        <div class="icon-circle"><i class="bi bi-briefcase"></i></div>
        <h3>Founders & Clients</h3>
        <p class="description">Validate ideas quickly, reduce upfront costs, and see clear deliverables.</p>
        <div class="votes">
          <button type="button" class="vote-btn up" title="Like" (click)="vote('audience-founders', 1)" [disabled]="isVoting('audience-founders')"><i class="bi bi-hand-thumbs-up"></i></button>
          <button type="button" class="vote-btn down" title="Dislike" (click)="vote('audience-founders', -1)" [disabled]="isVoting('audience-founders')"><i class="bi bi-hand-thumbs-down"></i></button>
          <span class="vote-count">{{ getTally('audience-founders') }}</span>
        </div>
      </div>
      <div class="card audience-card">
        <div class="icon-circle"><i class="bi bi-mortarboard"></i></div>
        <h3>Newcomers</h3>
        <p class="description">Guided workflows and sensible defaults from no‑code to pro‑code.</p>
        <div class="votes">
          <button type="button" class="vote-btn up" title="Like" (click)="vote('audience-newcomers', 1)" [disabled]="isVoting('audience-newcomers')"><i class="bi bi-hand-thumbs-up"></i></button>
          <button type="button" class="vote-btn down" title="Dislike" (click)="vote('audience-newcomers', -1)" [disabled]="isVoting('audience-newcomers')"><i class="bi bi-hand-thumbs-down"></i></button>
          <span class="vote-count">{{ getTally('audience-newcomers') }}</span>
        </div>
      </div>
      <div class="card audience-card">
        <div class="icon-circle"><i class="bi bi-people"></i></div>
        <h3>Teams & PMs</h3>
        <p class="description">Standardized workflows with a single source of truth across delivery.</p>
        <div class="votes">
          <button type="button" class="vote-btn up" title="Like" (click)="vote('audience-teams', 1)" [disabled]="isVoting('audience-teams')"><i class="bi bi-hand-thumbs-up"></i></button>
          <button type="button" class="vote-btn down" title="Dislike" (click)="vote('audience-teams', -1)" [disabled]="isVoting('audience-teams')"><i class="bi bi-hand-thumbs-down"></i></button>
          <span class="vote-count">{{ getTally('audience-teams') }}</span>
        </div>
      </div>
      <div class="card audience-card">
        <div class="icon-circle"><i class="bi bi-building"></i></div>
        <h3>Agencies</h3>
        <p class="description">Scale delivery, quote accurately, and manage predictable outcomes.</p>
        <div class="votes">
          <button type="button" class="vote-btn up" title="Like" (click)="vote('audience-agencies', 1)" [disabled]="isVoting('audience-agencies')"><i class="bi bi-hand-thumbs-up"></i></button>
          <button type="button" class="vote-btn down" title="Dislike" (click)="vote('audience-agencies', -1)" [disabled]="isVoting('audience-agencies')"><i class="bi bi-hand-thumbs-down"></i></button>
          <span class="vote-count">{{ getTally('audience-agencies') }}</span>
        </div>
      </div>
      <div class="card audience-card">
        <div class="icon-circle"><i class="bi bi-lightbulb"></i></div>
        <h3>Anyone with an idea</h3>
        <p class="description">From concept to MVP in minutes — then iterate with confidence.</p>
        <div class="votes">
          <button type="button" class="vote-btn up" title="Like" (click)="vote('audience-anyone', 1)" [disabled]="isVoting('audience-anyone')"><i class="bi bi-hand-thumbs-up"></i></button>
          <button type="button" class="vote-btn down" title="Dislike" (click)="vote('audience-anyone', -1)" [disabled]="isVoting('audience-anyone')"><i class="bi bi-hand-thumbs-down"></i></button>
          <span class="vote-count">{{ getTally('audience-anyone') }}</span>
        </div>
      </div>
      <div class="card audience-card">
        <div class="icon-circle"><i class="bi bi-person-workspace"></i></div>
        <h3>Freelancers</h3>
        <p class="description">Deliver more with less by automating the heavy lifting.</p>
        <div class="votes">
          <button type="button" class="vote-btn up" title="Like" (click)="vote('audience-freelancers', 1)" [disabled]="isVoting('audience-freelancers')"><i class="bi bi-hand-thumbs-up"></i></button>
          <button type="button" class="vote-btn down" title="Dislike" (click)="vote('audience-freelancers', -1)" [disabled]="isVoting('audience-freelancers')"><i class="bi bi-hand-thumbs-down"></i></button>
          <span class="vote-count">{{ getTally('audience-freelancers') }}</span>
        </div>
      </div>
      <div class="card audience-card">
        <div class="icon-circle"><i class="bi bi-buildings"></i></div>
        <h3>Enterprises</h3>
        <p class="description">Standardize delivery at scale with opinionated workflows and guardrails.</p>
        <div class="votes">
          <button type="button" class="vote-btn up" title="Like" (click)="vote('audience-enterprises', 1)" [disabled]="isVoting('audience-enterprises')"><i class="bi bi-hand-thumbs-up"></i></button>
          <button type="button" class="vote-btn down" title="Dislike" (click)="vote('audience-enterprises', -1)" [disabled]="isVoting('audience-enterprises')"><i class="bi bi-hand-thumbs-down"></i></button>
          <span class="vote-count">{{ getTally('audience-enterprises') }}</span>
        </div>
      </div>
    </div>
  </section>

  <section class="features">
    <h2>What it includes</h2>
    <div class="grid">
      <button type="button" class="feat" [class.active]="selectedKey === 'summary'" (click)="selectFeature('summary')">Summary</button>
      <button type="button" class="feat" [class.active]="selectedKey === 'define'" (click)="selectFeature('define')">Define</button>
      <button type="button" class="feat" [class.active]="selectedKey === 'design'" (click)="selectFeature('design')">Design</button>
      <button type="button" class="feat" [class.active]="selectedKey === 'db-design'" (click)="selectFeature('db-design')">DB Design</button>
      <button type="button" class="feat" [class.active]="selectedKey === 'guardrails'" (click)="selectFeature('guardrails')">Guardrails</button>
      <button type="button" class="feat" [class.active]="selectedKey === 'testing'" (click)="selectFeature('testing')">Testing</button>
      <button type="button" class="feat" [class.active]="selectedKey === 'devops'" (click)="selectFeature('devops')">Dev‑Ops</button>
      <button type="button" class="feat" [class.active]="selectedKey === 'docs'" (click)="selectFeature('docs')">Docs</button>
    </div>
    @if (selectedFeature; as f) {
      <div class="feature-detail">
        <div class="detail-grid">
          <div class="detail-text">
            <h3>{{ f.title }}</h3>
            <p class="short">{{ f.short }}</p>
            <ul>
              @for (p of f.points; track p) {
                <li>{{ p }}</li>
              }
            </ul>
            <div class="votes">
              <button type="button" class="vote-btn up" title="Like" (click)="vote('feature-' + f.key, 1)" [disabled]="isVoting('feature-' + f.key)"><i class="bi bi-hand-thumbs-up"></i></button>
              <button type="button" class="vote-btn down" title="Dislike" (click)="vote('feature-' + f.key, -1)" [disabled]="isVoting('feature-' + f.key)"><i class="bi bi-hand-thumbs-down"></i></button>
              <span class="vote-count">{{ getTally('feature-' + f.key) }}</span>
            </div>
          </div>
          @if (selectedImage; as img) {
            <div class="detail-image">
              <img [src]="img" [alt]="f.title + ' preview'">
            </div>
          }
        </div>
      </div>
    }
  </section>

  <section class="benefits">
    <h2>Why The Machine</h2>
    <p class="section-desc">
      Most AI tools help you prototype, then stall at integration, governance, security, and reliability. The Machine pairs AI acceleration with built‑in guardrails, tests, and delivery pipelines—plus optional Heroes who ensure your system design holds up—so you avoid the rebuild tax and keep momentum from MVP to enterprise.
    </p>
    <div class="grid">
      <div class="card">
        <h3>Ship 100× faster</h3>
        <p class="description">Automated handoffs from definition through deployment remove waiting and rework.</p>
        <div class="votes">
          <button type="button" class="vote-btn up" title="Like" (click)="vote('benefit-ship-fast', 1)" [disabled]="isVoting('benefit-ship-fast')"><i class="bi bi-hand-thumbs-up"></i></button>
          <button type="button" class="vote-btn down" title="Dislike" (click)="vote('benefit-ship-fast', -1)" [disabled]="isVoting('benefit-ship-fast')"><i class="bi bi-hand-thumbs-down"></i></button>
          <span class="vote-count">{{ getTally('benefit-ship-fast') }}</span>
        </div>
      </div>
      <div class="card">
        <h3>Reduce rework 30–70%</h3>
        <p class="description">Guardrails and test harnesses catch defects early and standardize quality.</p>
        <div class="votes">
          <button type="button" class="vote-btn up" title="Like" (click)="vote('benefit-reduce-rework', 1)" [disabled]="isVoting('benefit-reduce-rework')"><i class="bi bi-hand-thumbs-up"></i></button>
          <button type="button" class="vote-btn down" title="Dislike" (click)="vote('benefit-reduce-rework', -1)" [disabled]="isVoting('benefit-reduce-rework')"><i class="bi bi-hand-thumbs-down"></i></button>
          <span class="vote-count">{{ getTally('benefit-reduce-rework') }}</span>
        </div>
      </div>
      <div class="card">
        <h3>Scale without the rebuild tax</h3>
        <p class="description">Architecture, guardrails, and ops that grow from MVP to enterprise—no throwaway prototype.</p>
        <div class="votes">
          <button type="button" class="vote-btn up" title="Like" (click)="vote('benefit-scale', 1)" [disabled]="isVoting('benefit-scale')"><i class="bi bi-hand-thumbs-up"></i></button>
          <button type="button" class="vote-btn down" title="Dislike" (click)="vote('benefit-scale', -1)" [disabled]="isVoting('benefit-scale')"><i class="bi bi-hand-thumbs-down"></i></button>
          <span class="vote-count">{{ getTally('benefit-scale') }}</span>
        </div>
      </div>
      <div class="card">
        <h3>Predictable delivery</h3>
        <p class="description">Opinionated workflows create clarity from idea to release.</p>
        <div class="votes">
          <button type="button" class="vote-btn up" title="Like" (click)="vote('benefit-predictable', 1)" [disabled]="isVoting('benefit-predictable')"><i class="bi bi-hand-thumbs-up"></i></button>
          <button type="button" class="vote-btn down" title="Dislike" (click)="vote('benefit-predictable', -1)" [disabled]="isVoting('benefit-predictable')"><i class="bi bi-hand-thumbs-down"></i></button>
          <span class="vote-count">{{ getTally('benefit-predictable') }}</span>
        </div>
      </div>
    </div>
  </section>

  <section class="truths">
    <h2>What you should know</h2>
    <p class="section-desc">
      AI multiplies experienced teams—it doesn’t replace engineering discipline. With The Machine, automation handles the repetitive work while human review gates, standards, and tests safeguard quality. Assign Heroes when you want hands‑on support, and scale with confidence as your product grows.
    </p>
    <div class="grid">
      <div class="card">
        <h3>AI multiplies experienced teams</h3>
        <p class="description">AI accelerates delivery, while Heroes own reviews, integration, and system design for reliability.</p>
        <div class="votes">
          <button type="button" class="vote-btn up" title="Like" (click)="vote('truths-ai-multiplies', 1)" [disabled]="isVoting('truths-ai-multiplies')"><i class="bi bi-hand-thumbs-up"></i></button>
          <button type="button" class="vote-btn down" title="Dislike" (click)="vote('truths-ai-multiplies', -1)" [disabled]="isVoting('truths-ai-multiplies')"><i class="bi bi-hand-thumbs-down"></i></button>
          <span class="vote-count">{{ getTally('truths-ai-multiplies') }}</span>
        </div>
      </div>
      <div class="card">
        <h3>Avoid the “vibe coding” wall</h3>
        <p class="description">Skip the prototype‑to‑rebuild trap by combining AI with governance, security, and real engineering.</p>
        <div class="votes">
          <button type="button" class="vote-btn up" title="Like" (click)="vote('truths-avoid-vibe', 1)" [disabled]="isVoting('truths-avoid-vibe')"><i class="bi bi-hand-thumbs-up"></i></button>
          <button type="button" class="vote-btn down" title="Dislike" (click)="vote('truths-avoid-vibe', -1)" [disabled]="isVoting('truths-avoid-vibe')"><i class="bi bi-hand-thumbs-down"></i></button>
          <span class="vote-count">{{ getTally('truths-avoid-vibe') }}</span>
        </div>
      </div>
      <div class="card">
        <h3>Heroes on your team</h3>
        <p class="description">Optionally assign vetted senior engineers to your project. They guide architecture and delivery with The Machine.</p>
        <div class="votes">
          <button type="button" class="vote-btn up" title="Like" (click)="vote('truths-heroes', 1)" [disabled]="isVoting('truths-heroes')"><i class="bi bi-hand-thumbs-up"></i></button>
          <button type="button" class="vote-btn down" title="Dislike" (click)="vote('truths-heroes', -1)" [disabled]="isVoting('truths-heroes')"><i class="bi bi-hand-thumbs-down"></i></button>
          <span class="vote-count">{{ getTally('truths-heroes') }}</span>
        </div>
      </div>
      <div class="card">
        <h3>Scale with the power of AI</h3>
        <p class="description">From first commit to full scale: pipelines, tests, and observability evolve as your product grows.</p>
        <div class="votes">
          <button type="button" class="vote-btn up" title="Like" (click)="vote('truths-scale', 1)" [disabled]="isVoting('truths-scale')"><i class="bi bi-hand-thumbs-up"></i></button>
          <button type="button" class="vote-btn down" title="Dislike" (click)="vote('truths-scale', -1)" [disabled]="isVoting('truths-scale')"><i class="bi bi-hand-thumbs-down"></i></button>
          <span class="vote-count">{{ getTally('truths-scale') }}</span>
        </div>
      </div>
    </div>
  </section>

  <section class="faq">
    <h2>Frequently asked questions</h2>
    <p class="section-desc">
      Quick answers to common questions about The Machine, Heroes, and how delivery works.
    </p>
    <div class="faq-list">
      @for (f of faqs; track f.key; let i = $index) {
        <div class="faq-item">
          <button
            type="button"
            class="faq-question"
            (click)="toggleFaq(i)"
            [attr.aria-expanded]="openFaqIndex === i ? 'true' : 'false'"
            [attr.aria-controls]="'faq-panel-' + i"
          >
            <span class="faq-q">{{ f.question }}</span>
            <i class="bi" [class.bi-chevron-down]="true" [class.rotated]="openFaqIndex === i"></i>
          </button>
          <div
            class="faq-answer"
            role="region"
            [attr.id]="'faq-panel-' + i"
            [attr.aria-hidden]="openFaqIndex === i ? 'false' : 'true'"
            [class.open]="openFaqIndex === i"
          >
            <p>{{ f.answer }}</p>
          </div>
        </div>
      }
    </div>
  </section>

  <section class="secondary-cta mb-4">
    <h2>Get early access</h2>
    @if (interestCount !== null) {
      <p class="interested-count">{{ interestCount }} interested so far</p>
    }
    <form (ngSubmit)="submitInterest()" #interestForm2="ngForm" class="interest-form alt">
      <div class="row">
        <input
          name="email2"
          type="email"
          placeholder="your@email.com"
          required
          [(ngModel)]="email"
          [disabled]="submitting"
          />
        <button type="submit" [disabled]="submitting">Notify me</button>
      </div>
    </form>
  </section>

  <section class="features">
    <h2>Additional AI tools & extra features</h2>
    <div class="grid">
      <button type="button" class="feat" [class.active]="extraSelectedKey === 'quoting'" (click)="selectExtraFeature('quoting')">Quoting Automation</button>
      <button type="button" class="feat" [class.active]="extraSelectedKey === 'time-tracking'" (click)="selectExtraFeature('time-tracking')">Time Tracking</button>
      <button type="button" class="feat" [class.active]="extraSelectedKey === 'workspace'" (click)="selectExtraFeature('workspace')">Workspace Management</button>
      <button type="button" class="feat" [class.active]="extraSelectedKey === 'billing'" (click)="selectExtraFeature('billing')">Billing</button>
      <button type="button" class="feat" [class.active]="extraSelectedKey === 'user-management'" (click)="selectExtraFeature('user-management')">User Management</button>
      <button type="button" class="feat" [class.active]="extraSelectedKey === 'marketplace'" (click)="selectExtraFeature('marketplace')">Marketplace</button>
    </div>
    @if (extraSelectedFeature; as xf) {
      <div class="feature-detail">
        <div class="detail-grid">
          <div class="detail-text">
            <h3>{{ xf.title }}</h3>
            <p class="short">{{ xf.short }}</p>
            <ul>
              @for (p of xf.points; track p) {
                <li>{{ p }}</li>
              }
            </ul>
            <div class="votes">
              <button type="button" class="vote-btn up" title="Like" (click)="vote('extra-' + xf.key, 1)" [disabled]="isVoting('extra-' + xf.key)"><i class="bi bi-hand-thumbs-up"></i></button>
              <button type="button" class="vote-btn down" title="Dislike" (click)="vote('extra-' + xf.key, -1)" [disabled]="isVoting('extra-' + xf.key)"><i class="bi bi-hand-thumbs-down"></i></button>
              <span class="vote-count">{{ getTally('extra-' + xf.key) }}</span>
            </div>
          </div>
          @if (extraSelectedImage; as ximg) {
            <div class="detail-image">
              <img [src]="ximg" [alt]="xf.title + ' preview'">
            </div>
          }
        </div>
      </div>
    }
  </section>

  <!-- <section class="pricing">
    <h2>Plans that scale with you</h2>
    <div class="grid">
      <div class="card plan-card" (click)="selectPlan('starter')" [class.featured]="selectedPlan === 'starter'">
        <h3>Starter</h3>
        <p class="price"><span class="amount">$0</span><span class="period">/mo</span></p>
        <ul class="features-list">
          <li>AI‑assisted builder</li>
          <li>Standard guardrails</li>
          <li>Community support</li>
          <li>Limited features</li>
        </ul>
        <button type="button" class="plan-cta" [disabled]="true">Get started</button>
      </div>
      <div class="card plan-card" (click)="selectPlan('pro')" [class.featured]="selectedPlan === 'pro'">
        <h3>Pro</h3>
        <p class="price"><span class="amount">$99</span><span class="period">/mo</span></p>
        <ul class="features-list">
          <li>Everything in Starter</li>
          <li>Advanced guardrails & tests</li>
          <li>Hero check‑ins (2h/month)</li>
          <li>Priority support</li>
        </ul>
        <button type="button" class="plan-cta primary" [disabled]="true">Start Pro</button>
      </div>
      <div class="card plan-card" (click)="selectPlan('enterprise')" [class.featured]="selectedPlan === 'enterprise'">
        <h3>Enterprise</h3>
        <p class="price"><span class="amount">Custom</span></p>
        <ul class="features-list">
          <li>Dedicated Heroes & SRE</li>
          <li>Compliance & governance</li>
          <li>Private cloud & SSO</li>
          <li>Tailored SLAs</li>
        </ul>
        <button type="button" class="plan-cta" [disabled]="true">Talk to sales</button>
      </div>
    </div>
  </section> -->

  <!-- <section class="providers">
    <h2>Payment providers</h2>
    <div class="grid">
      <div class="card provider">
        <div class="provider-logo"></div>
        <div class="provider-name">Stripe</div>
      </div>
      <div class="card provider">
        <div class="provider-logo"></div>
        <div class="provider-name">PayPal</div>
      </div>
      <div class="card provider">
        <div class="provider-logo"></div>
        <div class="provider-name">Apple&nbsp;Pay</div>
      </div>
      <div class="card provider">
        <div class="provider-logo"></div>
        <div class="provider-name">Google&nbsp;Pay</div>
      </div>
      <div class="card provider">
        <div class="provider-logo"></div>
        <div class="provider-name">Adyen</div>
      </div>
      <div class="card provider">
        <div class="provider-logo"></div>
        <div class="provider-name">Braintree</div>
      </div>
    </div>
    <p class="providers-note">Icons coming soon. Configure multiple providers per workspace.</p>
  </section> -->

  <!-- <section class="results-summary mb-4">
    <h2>What people are interested in</h2>
    <h3>Who it's for</h3>
    <div class="grid">
      @for (it of getAudienceSummary(); track it.key) {
        <div class="card stat">
          <div class="stat-label">{{ it.label }}</div>
          <div class="stat-count"><span class="vote-count">{{ it.count }}</span></div>
        </div>
      }
    </div>
    <h3>Why The Machine</h3>
    <div class="grid">
      @for (it of getBenefitSummary(); track it.key) {
        <div class="card stat">
          <div class="stat-label">{{ it.label }}</div>
          <div class="stat-count"><span class="vote-count">{{ it.count }}</span></div>
        </div>
      }
    </div>
    <h3>What you should know</h3>
    <div class="grid">
      @for (it of getTruthsSummary(); track it.key) {
        <div class="card stat">
          <div class="stat-label">{{ it.label }}</div>
          <div class="stat-count"><span class="vote-count">{{ it.count }}</span></div>
        </div>
      }
    </div>
    <h3>What it includes</h3>
    <div class="grid">
      @for (it of getFeatureSummary(); track it.key) {
        <div class="card stat">
          <div class="stat-label">{{ it.label }}</div>
          <div class="stat-count"><span class="vote-count">{{ it.count }}</span></div>
        </div>
      }
    </div>
    <h3>Additional AI tools & extra features</h3>
    <div class="grid">
      @for (it of getExtraSummary(); track it.key) {
        <div class="card stat">
          <div class="stat-label">{{ it.label }}</div>
          <div class="stat-count"><span class="vote-count">{{ it.count }}</span></div>
        </div>
      }
    </div>
  </section> -->
</main>

<app-toast-container></app-toast-container>

<footer class="landing-footer">
  <div class="container">
    <span class="copyright">© {{ currentYear }} The Machine</span>
    <span class="powered-by">Powered by <img src="/appstrax-logo.png" alt="Appstrax" class="powered-logo"></span>
  </div>
</footer>


